import { Alert, Button, Loader, Select } from '@piggytechng/component-library';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { OverideButtonStyle, OverideSelectStyle } from '../../../../helpers/utils';
import useAccountState from '../../../../hooks/useAccountState';
import useRolePermissions from '../../../../hooks/useRolePermissions';
import ModalExecuteAction from '../../../../components/Modals/ModalExecuteAction';
import NoData from '../../../../components/NoData/NoData';
import { localStorageEnum } from '../../../../api/enums';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { setModalActions } from '../../../../store/slices/utils';
import { AccountStateHistoryItem } from '../../../../types';

// `at` is an ISO-8601 string from core; tolerate unix seconds / milliseconds too.
export const formatStateDate = (at: number | string | null | undefined) => {
  if (at === null || at === undefined || at === '') return 'N/A';
  const parsed = typeof at === 'number' ? (at > 1e12 ? dayjs(at) : dayjs.unix(at)) : dayjs(at);
  return parsed.isValid() ? parsed.format('D MMM YYYY | h:mm A') : 'N/A';
};

export default function AccountStatusManager({ UserId }: any) {
  const [statusTab, setStatusTab] = useState<'deactivate' | 'reactivate'>('deactivate');
  const [reason, setReason] = useState('');
  const [showModal, setShowModal] = useState(false);
  const { accountState, isLoadingAccountState, deactivateAccount, reactivateAccount, isRunningAccountStateAction } =
    useAccountState(UserId);
  const { isSuperAdmin } = useRolePermissions({});
  const modalActions = useSelector((state: RootState) => state.modalActions);
  const dispatch = useDispatch();

  const style = { ...OverideSelectStyle, style: { ...OverideSelectStyle.style, zIndex: undefined } };

  const userName = `${localStorage.getItem(localStorageEnum['local.storage.firstName'])} ${localStorage.getItem(localStorageEnum['local.storage.lastName'])}`;

  const isDeactivated = accountState?.state === 'deactivated';
  const history: AccountStateHistoryItem[] = accountState?.history ?? [];

  useEffect(() => {
    setReason('');
  }, [statusTab]);

  // Land on the sub-tab that can actually change something for this account.
  useEffect(() => {
    if (accountState) setStatusTab(accountState.state === 'deactivated' ? 'reactivate' : 'deactivate');
  }, [accountState?.state]);

  const resetForm = () => {
    setReason('');
    setShowModal(false);
  };

  const openConfirmation = () => {
    if (!isSuperAdmin) {
      Alert.warn('You do not have access to perform this action.');
      return;
    }
    if (!reason.trim()) {
      Alert.error(
        statusTab === 'deactivate'
          ? 'Kindly enter why the account is being deactivated.'
          : 'Kindly enter why the account is being reactivated.',
      );
      return;
    }
    if (statusTab === 'deactivate' && isDeactivated) {
      Alert.error('This account is already deactivated.');
      return;
    }
    if (statusTab === 'reactivate' && accountState && !isDeactivated) {
      Alert.error('This account is already active.');
      return;
    }
    setShowModal(true);
  };

  const statusLine = () => {
    if (isLoadingAccountState) return 'Checking account status...';
    if (!accountState) return 'Account status is unavailable right now.';
    if (isDeactivated) {
      return `This account is deactivated since ${formatStateDate(accountState.at)} by ${
        accountState.adminName || 'an admin'
      }: ${accountState.reason || 'no reason recorded'}`;
    }
    return 'Account is active.';
  };

  return (
    <div className="account-status-manager">
      <div className="account-status-manager__tabs">
        <div
          className={statusTab === 'deactivate' ? 'active' : ''}
          onClick={() => {
            !isRunningAccountStateAction && setStatusTab('deactivate');
          }}
        >
          Deactivate Account
        </div>
        <div
          className={statusTab === 'reactivate' ? 'active' : ''}
          onClick={() => !isRunningAccountStateAction && setStatusTab('reactivate')}
        >
          Reactivate Account
        </div>
      </div>

      <div className="account-status-manager__content">
        <p className={`account-status-manager__content__status ${isDeactivated ? 'deactivated' : ''}`}>
          {statusLine()}
        </p>

        <div className="reason-field">
          <p>{statusTab === 'deactivate' ? 'Deactivation Reason' : 'Reactivation Note'}</p>
          <textarea
            name="account-status-reason"
            placeholder={
              statusTab === 'deactivate' ? 'Enter deactivation reason' : 'Enter reason for reactivating the account'
            }
            rows={4}
            cols={50}
            maxLength={255}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <small className="reason-field__count">{reason.length}/255</small>
        </div>

        <Select
          label={statusTab === 'deactivate' ? 'Deactivated By' : 'Reactivated By'}
          {...style}
          labelStyle={{ color: '#666766' }}
          backgroundColor="#F9FAFB"
          outlineColor="none"
          height="60px"
          placeholder=""
          value={userName}
          key={reason}
          style={{
            pointerEvents: 'none',
          }}
          options={[
            {
              value: userName,
              label: userName,
            },
          ]}
        />

        <div className="account-status-manager__content__actions">
          <Button
            type="submit"
            {...OverideButtonStyle}
            disabled={isRunningAccountStateAction}
            style={{
              fontFamily: 'Jakarta',
              color: '#212337',
              flex: 1,
              margin: '10px 0 20px',
            }}
            color="#F7F7F7"
            onClick={resetForm}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            {...OverideButtonStyle}
            loading={isRunningAccountStateAction}
            style={{
              fontFamily: 'Jakarta',
              flex: 1,
            }}
            onClick={openConfirmation}
          >
            {statusTab === 'deactivate' ? 'Deactivate Account' : 'Reactivate Account'}
          </Button>
        </div>
      </div>

      <div className="account-status-manager__history">
        <h3>Account Status History</h3>
        {isLoadingAccountState ? (
          <div className="page-loader">
            <Loader color="#0F60D6" />
          </div>
        ) : history.length === 0 ? (
          <NoData message="No account status changes found." />
        ) : (
          <div className="table-container account-status-history">
            <table className="table">
              <thead className="table_head">
                <tr>
                  <th className="date">Date</th>
                  <th className="type">Action</th>
                  <th>Admin</th>
                  <th className="memo">Reason</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {history.map((item, idx) => (
                  <tr key={item.id ?? `${item.at ?? 'na'}-${idx}`} className="table-row">
                    <td className="date">{formatStateDate(item.at)}</td>
                    <td className="type">{item.state === 'deactivated' ? 'Deactivated' : 'Reactivated'}</td>
                    <td>{item.adminName || item.adminUserId || 'N/A'}</td>
                    <td className="memo">{item.reason || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ModalExecuteAction
        modalIsOpen={showModal}
        setIsOpen={(state: boolean) => {
          setShowModal(state);
          if (!state) {
            dispatch(setModalActions({ ...modalActions, password: '' }));
          }
        }}
        action={(password: string) => {
          if (statusTab === 'deactivate') {
            deactivateAccount(reason.trim(), password, () => resetForm());
          } else {
            reactivateAccount(reason.trim(), password, () => resetForm());
          }
        }}
        text={statusTab === 'deactivate' ? 'deactivate this account?' : 'reactivate this account?'}
        actionColor={'#1734ca'}
        isLoading={isRunningAccountStateAction}
      />
    </div>
  );
}
